import { filter, map, sortBy, uniq } from "lodash-es";
import { observer } from "mobx-react-lite";

import { MDSIconKind } from "@/mds";
import { useAgentOverlayAnalytics } from "@/pages/agent/analytics/use-agent-overlay-analytics";
import { AgentFilterSearchField } from "@/pages/agent/components/agent-filter-search-field";
import { AgentFilterSelect } from "@/pages/agent/components/agent-filter-select";
import { AgentPageHeader } from "@/pages/agent/components/agent-page-header";
import { AgentPageFilterBar, AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import {
  AgentWorkEmptyState,
  AgentWorkEmptyStateKind,
} from "@/pages/agent/components/agent-work-empty-state";
import { buildProjectSortFilterItems } from "@/pages/agent/projects/agent-project-filter-items";
import {
  buildProjectCards,
  DEFAULT_PROJECT_STATUS_FILTER_VALUES,
  getSortedProjectCards,
  ProjectSectionKind,
} from "@/pages/agent/projects/agent-projects-page.utils";
import { AgentProjectsSection } from "@/pages/agent/projects/agent-projects-section";
import {
  ALL_PROJECT_STATUSES_FILTER,
  DEFAULT_PROJECT_SORT,
  useAgentProjectsSearchParams,
} from "@/pages/agent/projects/use-agent-projects-search-params";
import {
  buildAgentStatusFilterItems,
  formatStatusLabel,
  normalizeStatusValue,
} from "@/pages/agent/utils/agent-status.utils";
import { isClosedAssistantProjectStatus } from "@/store/assistant/assistant-project-status";
import { useAppStore } from "@/store";

export const AgentProjectsPage = observer(() => {
  const { store } = useAppStore();
  const analytics = useAgentOverlayAnalytics();
  const { statusFilter, searchQuery, sortFilter, updateProjectFilters } =
    useAgentProjectsSearchParams();
  const projects = store.assistantProjects.currentAssistantModels;
  const tasks = store.assistantTasks.currentAssistantModels;
  const followUps = store.assistantFollowUps.currentAssistantModels;
  const dataLoaded =
    store.assistantProjects.currentAssistantModelsLoaded &&
    store.assistantTasks.currentAssistantModelsLoaded &&
    store.assistantFollowUps.currentAssistantModelsLoaded;

  const projectCards = buildProjectCards({
    projects,
    tasks,
    followUps,
  });

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredProjectCards = filter(projectCards, projectCard => {
    const matchesStatus =
      statusFilter === ALL_PROJECT_STATUSES_FILTER ||
      normalizeStatusValue({ status: projectCard.project.status }) === statusFilter;
    const matchesSearch =
      !normalizedSearchQuery || projectCard.searchableText.includes(normalizedSearchQuery);

    return matchesStatus && matchesSearch;
  });

  const activeProjectCards = getSortedProjectCards({
    projectCards: filter(filteredProjectCards, projectCard => !projectCard.isClosed),
    sortKey: sortFilter,
  });
  const completedProjectCards = getSortedProjectCards({
    projectCards: filter(filteredProjectCards, projectCard => projectCard.isClosed),
    sortKey: sortFilter,
  });
  const isFilteringByStatus = statusFilter !== ALL_PROJECT_STATUSES_FILTER;
  const statusFilteredProjectCards = getSortedProjectCards({
    projectCards: filteredProjectCards,
    sortKey: sortFilter,
  });
  const statusFilteredSectionKind = isClosedAssistantProjectStatus({ status: statusFilter })
    ? ProjectSectionKind.Completed
    : ProjectSectionKind.Active;
  const statusFilteredSectionTitle = `${formatStatusLabel({ status: statusFilter })} projects`;

  const sortFilterItems = buildProjectSortFilterItems();

  const statusFilterValues = sortBy(
    uniq([
      ...DEFAULT_PROJECT_STATUS_FILTER_VALUES,
      ...map(projectCards, projectCard =>
        normalizeStatusValue({ status: projectCard.project.status })
      ),
    ]),
    status => formatStatusLabel({ status })
  );
  const statusFilterItems = buildAgentStatusFilterItems({
    allStatusesFilter: ALL_PROJECT_STATUSES_FILTER,
    statusFilter,
    statusFilterValues,
  });
  const queryKey = JSON.stringify({
    searchQuery: normalizedSearchQuery,
    sortFilter,
    statusFilter,
  });
  const showCreationGuidance = dataLoaded && projectCards.length === 0;

  const openProject = ({ projectId }: { projectId: string }) => {
    analytics.trackItemClick({
      itemId: projectId,
      itemKind: "project",
      source: "project_card",
    });
    store.agentOverlay.openProject({ projectId });
  };
  const sectionActions = {
    onOpenActivities: ({ projectId }: { projectId: string }) => {
      store.agentOverlay.openActivities({ kind: "project", projectId });
    },
    onOpenProject: openProject,
    onOpenProjectTasks: ({ projectId }: { projectId: string }) => {
      analytics.trackItemClick({
        itemId: projectId,
        itemKind: "project",
        source: "project_card",
      });
      store.agentOverlay.openProjectTasks({ projectId });
    },
    onOpenTask: ({ taskId }: { taskId: string }) => {
      analytics.trackItemClick({
        itemId: taskId,
        itemKind: "task",
        source: "project_next_up",
      });
      store.agentOverlay.openTask({ taskId });
    },
  };

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Projects}>
      <AgentPageHeader
        description="Active work, the tasks moving it forward, and the outcomes it supports."
        title="Projects"
      />

      {showCreationGuidance ? null : (
        <AgentPageFilterBar>
          <AgentFilterSelect
            label="Sort"
            selectedKey={sortFilter}
            defaultKey={DEFAULT_PROJECT_SORT}
            items={sortFilterItems}
            leadingIcon={MDSIconKind.SortAlt}
            alwaysActive
            onSelectionChange={({ key }) => {
              analytics.trackFilterChange({ filterKind: "sort", filterValue: key });
              updateProjectFilters({ nextSortFilter: key });
            }}
            onClear={() => {
              analytics.trackFilterChange({
                filterKind: "sort",
                filterValue: DEFAULT_PROJECT_SORT,
              });
              updateProjectFilters({ nextSortFilter: DEFAULT_PROJECT_SORT });
            }}
          />

          {/* No `leadingIcon`: each status has its own icon, so the trigger shows
            the selected one rather than a generic circle. */}
          <AgentFilterSelect
            label="Status"
            selectedKey={statusFilter}
            defaultKey={ALL_PROJECT_STATUSES_FILTER}
            items={statusFilterItems}
            onSelectionChange={({ key }) => {
              analytics.trackFilterChange({ filterKind: "status", filterValue: key });
              updateProjectFilters({ nextStatusFilter: key });
            }}
            onClear={() => {
              analytics.trackFilterChange({
                filterKind: "status",
                filterValue: ALL_PROJECT_STATUSES_FILTER,
              });
              updateProjectFilters({ nextStatusFilter: ALL_PROJECT_STATUSES_FILTER });
            }}
          />

          <AgentFilterSearchField
            ariaLabel="Filter projects by name"
            placeholder="Filter by name"
            value={searchQuery}
            onChange={value => {
              analytics.trackFilterChange({
                filterKind: "search",
                queryLength: value.trim().length,
              });
              updateProjectFilters({ nextSearchQuery: value });
            }}
          />
        </AgentPageFilterBar>
      )}

      {showCreationGuidance ? (
        <AgentWorkEmptyState kind={AgentWorkEmptyStateKind.Projects} />
      ) : isFilteringByStatus ? (
        <AgentProjectsSection
          {...sectionActions}
          title={statusFilteredSectionTitle}
          kind={statusFilteredSectionKind}
          dataLoaded={dataLoaded}
          projectCards={statusFilteredProjectCards}
          queryKey={queryKey}
        />
      ) : (
        <>
          <AgentProjectsSection
            {...sectionActions}
            title="Active projects"
            kind={ProjectSectionKind.Active}
            dataLoaded={dataLoaded}
            projectCards={activeProjectCards}
            queryKey={queryKey}
          />

          <AgentProjectsSection
            {...sectionActions}
            title="Completed projects"
            kind={ProjectSectionKind.Completed}
            dataLoaded={dataLoaded}
            projectCards={completedProjectCards}
            queryKey={queryKey}
          />
        </>
      )}
    </AgentPageLayout>
  );
});
